import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Card from '../components/card/Card';
import H from '../components/texts/h/H';
import P from '../components/texts/p/P';
import Button from '../components/button/Button';
import IconButton from '../components/icon-button/IconButton';
import TableSkeleton from '../components/TableSkeleton/TableSkeleton';
import {
  getProtectorDetail,
  updateProtector,
  getProtectorFormMetadata,
  getProtectorInstallmentConfrontations,
} from '../services/GuaranteesService';
import { routes } from '../routes/routePaths';
import ProtectorDetailsBasicSection from '../organisms/Protectors/ProtectorDetailsBasicSection';
import { Toast, useToast } from '../components/toast/toast';
import { normalizeDateOnlyFromApi } from '../utils/dateOnly';

interface ProtectorDetailData {
  id: number;
  company_name: string;
  document_type_id: number;
  document_type_name: string;
  legal_entity_type_id: number;
  legal_entity_type_name: string;
  cuit: string;
  email: string;
  meeting_minute_type_id: number;
  meeting_minute_type_name: string;
  minute_number: string;
  registration_date: string;
  enabled: boolean;
  created_at: string;
  updated_at: string;
}

interface FormMetadata {
  document_types: Array<{ id: number; name: string }>;
  legal_entity_types: Array<{ id: number; name: string }>;
  meeting_minute_types: Array<{ id: number; name: string }>;
}

interface FormValues {
  company_name: string;
  cuit: string;
  email: string;
  registration_date: string;
  document_type_id: number | null;
  legal_entity_type_id: number | null;
  meeting_minute_type_id: number | null;
  minute_number: string;
}

export const MINUTE_NUMBER_ERROR_MSG = 'El número de acta ya está asignado a otro protector.';

const isMinuteNumberDuplicateError = (message: string): boolean =>
  message.includes('número de acta ya está asignado');

export const getFormValidationErrors = (values: FormValues | null): string[] => {
  if (!values) return [];

  const errors: string[] = [];

  if (!values.company_name.trim()) errors.push('La razón social es obligatoria');
  if (!values.document_type_id) errors.push('El tipo de documento es obligatorio');
  if (!values.cuit.trim()) errors.push('El documento es obligatorio');
  if (!values.email.trim()) errors.push('El email es obligatorio');
  if (!values.registration_date) errors.push('La fecha es obligatoria');
  if (!values.legal_entity_type_id) errors.push('La entidad legal es obligatoria');
  if (!values.meeting_minute_type_id) errors.push('El tipo de acta es obligatorio');
  if (!values.minute_number.trim()) errors.push('El número de acta es obligatorio');

  return errors;
};

const ProtectorEdit = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [protectorData, setProtectorData] = useState<ProtectorDetailData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [formValues, setFormValues] = useState<FormValues | null>(null);
  const [metadata, setMetadata] = useState<FormMetadata | null>(null);
  const [hasMoras, setHasMoras] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [minuteNumberError, setMinuteNumberError] = useState<string | null>(null);

  useEffect(() => {
    if (id) {
      loadAll();
    }
  }, [id]);

  const loadAll = async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const [detailRes, metaRes, morasRes] = await Promise.all([
        getProtectorDetail(id),
        getProtectorFormMetadata(),
        getProtectorInstallmentConfrontations(id, 0, 1),
      ]);

      if (detailRes.success && 'data' in detailRes) {
        const data = detailRes.data;
        const normalized = {
          ...data,
          registration_date:
            normalizeDateOnlyFromApi(data.registration_date) ||
            data.registration_date ||
            '',
        };
        setProtectorData(normalized);
        setFormValues({
          company_name: normalized.company_name || '',
          cuit: normalized.cuit || '',
          email: normalized.email || '',
          registration_date: normalized.registration_date || '',
          document_type_id: normalized.document_type_id || null,
          legal_entity_type_id: normalized.legal_entity_type_id || null,
          meeting_minute_type_id: normalized.meeting_minute_type_id || null,
          minute_number: normalized.minute_number || '',
        });
      } else {
        setError('Error al cargar los datos del protector');
      }

      if (metaRes.success && 'data' in metaRes) {
        setMetadata(metaRes.data);
      }

      if (morasRes.success && 'data' in morasRes) {
        setHasMoras((morasRes.data.total_elements || 0) > 0);
      }
    } catch (err) {
      setError('Error al cargar los datos del protector');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const validateForm = (): boolean => {
    const errors = getFormValidationErrors(formValues);
    setValidationErrors(errors);
    return errors.length === 0;
  };

  const isFormComplete = (): boolean => {
    if (!formValues) return false;
    return getFormValidationErrors(formValues).length === 0 && !minuteNumberError;
  };

  const showSaveErrorToast = (title: string) => {
    useToast.error({
      title,
      id: 'edit-error',
      description: '',
      closeButton: true,
      autoClose: 5000,
      toastId: 'edit-error',
    });
  };

  const handleSave = async () => {
    if (!validateForm() || !formValues || !id) return;

    setSubmitting(true);
    setMinuteNumberError(null);
    try {
      const cleanedCuit = (protectorData.cuit || '')
        .replace(/-/g, '')
        .replace(/\s/g, '');
      const requestBody = {
        companyName: formValues.company_name,
        documentTypeId: formValues.document_type_id,
        legalEntityTypeId: formValues.legal_entity_type_id,
        cuit: cleanedCuit,
        email: formValues.email,
        meetingMinuteTypeId: formValues.meeting_minute_type_id,
        minuteNumber: formValues.minute_number,
        registrationDate:
          normalizeDateOnlyFromApi(formValues.registration_date) ||
          formValues.registration_date,
      };

      const response = await updateProtector(id, requestBody);

      if (response.success) {
        useToast.success({ title: 'Datos del protector actualizados correctamente', id: 'edit-success', description: '', closeButton: true, autoClose: 5000, toastId: 'edit-success' });
        navigate(routes.protectorDetail.replace(':id', id));
      } else {
        const errorMessage =
          ('message' in response ? response.message : null) ||
          'Error al actualizar el protector';
        if (isMinuteNumberDuplicateError(errorMessage)) {
          setMinuteNumberError(MINUTE_NUMBER_ERROR_MSG);
        } else {
          showSaveErrorToast(errorMessage);
        }
      }
    } catch (err: any) {
      const errorMessage =
        err?.response?.data?.message || err?.message || 'Error al actualizar el protector';
      if (isMinuteNumberDuplicateError(errorMessage)) {
        setMinuteNumberError(MINUTE_NUMBER_ERROR_MSG);
      } else {
        showSaveErrorToast(errorMessage);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    if (!id) {
      navigate(routes.fundsAndProtectors);
      return;
    }
    navigate(routes.protectorDetail.replace(':id', id));
  };

  if (loading) {
    return (
      <Card variant='neutral' modifier='inverted' spacing={'4'} height='-webkit-fill-available' width='85vw'>
        <TableSkeleton rows={10} columns={4} />
      </Card>
    );
  }

  if (error || !protectorData) {
    return (
      <Card variant='neutral' modifier='inverted' spacing={'4'} height='-webkit-fill-available' width='85vw'>
        <Card display='flex' justifyContent='center' alignItems='center' height='300px'>
          <P type="1" weight="regular" modifier="weak">{error || 'Protector no encontrado'}</P>
        </Card>
      </Card>
    );
  }

  return (
    <Card variant='neutral' modifier='inverted' spacing={'4'} height='-webkit-fill-available' width='85vw'>
      <Card display='flex' alignItems='center' justifyContent='space-between' margin={{ bottom: '4' }}>
        <Card display='flex' alignItems='center' gap='16px'>
          <IconButton
            name='chevron_left_outline'
            ariaLabel='Volver al detalle'
            variant='primary'
            modifier='regular'
            size={24}
            onClick={handleCancel}
          />
          <H withMargin={false} type='1' weight='bold' modifier='strong'>
            Editar protector
          </H>
        </Card>
      </Card>

      <ProtectorDetailsBasicSection
        protector={protectorData}
        isEditMode={true}
        formValues={formValues}
        metadata={metadata}
        hasMoras={hasMoras}
        validationErrors={validationErrors}
        minuteNumberError={minuteNumberError}
        onFormChange={setFormValues}
        onMinuteNumberErrorClear={() => setMinuteNumberError(null)}
      />

      <Card display='flex' justifyContent='flex-end' margin={{ top: '4' }}>
        <Button
          variant='primary'
          onClick={handleSave}
          disabled={!isFormComplete() || submitting}
        >
          {submitting ? 'Guardando...' : 'Guardar'}
        </Button>
      </Card>

      <Toast id="protector-edit-toast" position="top-right" />
    </Card>
  );
};

export default ProtectorEdit;
